import { observer } from 'mobx-react-lite'
import { TextAreaContainer } from '@/components/inputs/TextAreaContainer'
import { match } from '@/utils/pattern-match-util'
import { JobScopeCustomItemContainer } from '@/features/jobs/details/components/UpdateJobScopeDialog/ListItems/JobScopeCustomItem/JobScopeCustomItemContainer'
import { JobScopeInventoryItemContainer } from '@/features/jobs/details/components/UpdateJobScopeDialog/ListItems/JobScopeInventoryItem/JobScopeInventoryItemContainer'
import { ManageItemsContainer } from '@/features/jobs/details/components/UpdateJobScopeDialog/ManageItems/ManageItemsContainer'
import { type UpdateJobScopeDialogViewModel } from './UpdateJobScopeDialogViewModel'
import {
  UpdateJobScopeDialog,
  UpdateJobScopeFailedStep,
  UpdateJobScopeItemsStep,
  UpdateJobScopeReasonStep,
  UpdateJobScopeSuccessStep,
} from '@/features/jobs/details/components/UpdateJobScopeDialog'

export interface UpdateJobScopeDialogContainerProps {
  vm: UpdateJobScopeDialogViewModel
}

export const UpdateJobScopeDialogContainer = observer(
  function UpdateJobScopeDialogContainer({
    vm,
  }: UpdateJobScopeDialogContainerProps) {
    const disabled = vm.submit.pending || vm.disabled

    return (
      <UpdateJobScopeDialog
        showing={vm.modal.showing}
        isSubmitPending={vm.submit.pending}
        onClose={vm.modal.close}
      >
        {match(vm.state, {
          UpdateJobScope: () => (
            <UpdateJobScopeItemsStep
              isEmpty={vm.isNoServiceItemsOnJob}
              isDisabled={disabled}
              onAddMore={vm.manageItemsVm.modal.show}
              onClose={vm.modal.close}
              onNext={vm.moveToReasonStep}
            >
              {vm.visibleInventoryItems.map((item) => {
                return (
                  <JobScopeInventoryItemContainer
                    key={item.versionId}
                    vm={item}
                    itemTestId={item.id}
                  />
                )
              })}
              {vm.selectedCustomItems.map((item) => {
                return (
                  <JobScopeCustomItemContainer
                    key={item.id}
                    vm={item}
                    onRemove={vm.removeCustomItem}
                    itemTestId={item.name}
                  />
                )
              })}
            </UpdateJobScopeItemsStep>
          ),
          SpecifyReason: () => (
            <UpdateJobScopeReasonStep
              reasonField={
                <TextAreaContainer
                  label="Reason"
                  model={vm}
                  field="reason"
                  name="reason"
                  data-test="job-scope-update-reason-input"
                  help={`${vm.reasonCharsRemaining} characters remaining.`}
                  focusOnError
                  placeholder="The reason for updating the job scope. Optional."
                  error={vm.validation.getError('reason')}
                />
              }
              isDisabled={disabled}
              isSubmitPending={vm.submit.pending}
              onBack={vm.backToItemsStep}
              onSubmit={vm.submit}
            />
          ),
          Success: () => <UpdateJobScopeSuccessStep onClose={vm.modal.close} />,
          Failed: () => (
            <UpdateJobScopeFailedStep
              error={vm.error}
              isDisabled={disabled}
              onClose={vm.modal.close}
            />
          ),
        })}
        <ManageItemsContainer vm={vm.manageItemsVm} />
      </UpdateJobScopeDialog>
    )
  },
)
